<script setup>
import { ref, computed, onMounted, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import Draggable from 'vuedraggable';
import { useAlert } from 'dashboard/composables';
import { useAdmin } from 'dashboard/composables/useAdmin';
import { useUISettings } from 'dashboard/composables/useUISettings';
import { getUserPermissions } from 'dashboard/helper/permissionsHelper';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import Select from 'dashboard/components-next/select/Select.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import DropdownMenu from 'dashboard/components-next/dropdown-menu/DropdownMenu.vue';
import PageHeader from 'dashboard/components-next/page-header/PageHeader.vue';
import PipelinesAPI from 'dashboard/api/pipelines';
import LeadsAPI from 'dashboard/api/leads';
import LeadCard from '../components/LeadCard.vue';
import PipelineWidgets from '../components/PipelineWidgets.vue';
import {
  unwrap,
  formatLeadCurrency,
  downloadBlob,
  periodDateRange,
  PERIOD_OPTIONS,
  LEADS_PER_PAGE,
} from '../helper';

const route = useRoute();
const router = useRouter();
const store = useStore();
const { t } = useI18n();
const { isAdmin } = useAdmin();
const { updateUISettings, uiSettings } = useUISettings();

const isLoading = ref(true);
const pipeline = ref(null);
const pipelines = ref([]);
const columns = ref([]); // [{ id, stage, leads: [] }]

const isExporting = ref(false);
const archivedView = ref(false); // tablero de archivados (solo admin)

// Móvil: se muestra UNA etapa a la vez (chips arriba). Mover = botón explícito.
const activeStageId = ref(null);
const moveDialog = ref(null);
const leadToMove = ref(null);

// ¿El usuario ve TODOS los leads? (admin / agente sin rol restrictivo). Si no,
// se oculta el filtro por agente (solo vería los suyos). Espeja LeadPolicy.
const accountPermissions = computed(
  () =>
    getUserPermissions(
      store.getters.getCurrentUser,
      Number(route.params.accountId)
    ) || []
);
const hasFullLeadAccess = computed(() => {
  const perms = accountPermissions.value;
  if (perms.includes('administrator') || perms.includes('conversation_manage'))
    return true;
  return !(
    perms.includes('conversation_participating_manage') ||
    perms.includes('conversation_unassigned_manage')
  );
});

// Selector + gestión de pipelines (multi-pipeline).
const showPipelineMenu = ref(false);
const pipelineDialog = ref(null);
const deletePipelineDialog = ref(null);
const pipelineDialogMode = ref('create'); // 'create' | 'rename'
const pipelineNameInput = ref('');

const deleteStageDialog = ref(null);
const stageToDelete = ref(null);
const editingStageId = ref(null);
const editingStageName = ref('');

// Filtros del tablero (búsqueda + agente + estado). En móvil se colapsan.
const search = ref('');
const assigneeFilter = ref('');
const statusFilter = ref('');
// Filtros seteados al navegar desde un widget (rango de creación + sin-tarea).
const createdFrom = ref('');
const createdTo = ref('');
const hasPendingTask = ref('');
// Periodo de los widgets (el selector vive en el header, visible solo con widgets).
const period = ref('today');
const filtersDialog = ref(null); // modal de filtros en móvil

const accountId = computed(() => route.params.accountId);

const agents = computed(() => store.getters['agents/getAgents'] || []);

const assigneeOptions = computed(() => [
  { value: '', label: t('LEADS.FILTER.ALL_AGENTS') },
  { value: 'unassigned', label: t('LEADS.FILTER.UNASSIGNED') },
  ...agents.value.map(agent => ({ value: agent.id, label: agent.name })),
]);

const statusOptions = computed(() => [
  { value: '', label: t('LEADS.FILTER.ALL_STATUS') },
  { value: 'open', label: t('LEADS.FILTER.STATUS_OPEN') },
  { value: 'won', label: t('LEADS.FILTER.STATUS_WON') },
  { value: 'lost', label: t('LEADS.FILTER.STATUS_LOST') },
]);

const hasActiveFilters = computed(
  () => !!search.value || !!assigneeFilter.value || !!statusFilter.value
);

// Menú del selector: sección 1 = cambiar de pipeline; sección 2 (admin) = gestión.
const pipelineMenuSections = computed(() => {
  const sections = [
    {
      items: pipelines.value.map(p => ({
        label: p.name,
        action: 'switch',
        value: p.id,
        icon: p.is_default ? 'i-lucide-star' : 'i-lucide-kanban',
      })),
    },
  ];
  if (isAdmin.value) {
    const current = pipeline.value;
    const adminItems = [
      {
        label: t('LEADS.PIPELINE.NEW'),
        action: 'create',
        value: 'create',
        icon: 'i-lucide-plus',
      },
      {
        label: t('LEADS.PIPELINE.RENAME'),
        action: 'rename',
        value: 'rename',
        icon: 'i-lucide-pencil',
      },
    ];
    if (current && !current.is_default) {
      adminItems.push({
        label: t('LEADS.PIPELINE.SET_DEFAULT'),
        action: 'set-default',
        value: 'set-default',
        icon: 'i-lucide-star',
      });
    }
    if (current && !current.is_default && pipelines.value.length > 1) {
      adminItems.push({
        label: t('LEADS.PIPELINE.DELETE'),
        action: 'delete',
        value: 'delete',
        icon: 'i-lucide-trash-2',
      });
    }
    adminItems.push({
      label: archivedView.value
        ? t('LEADS.PIPELINE.VIEW_ACTIVE')
        : t('LEADS.PIPELINE.VIEW_ARCHIVED'),
      action: 'toggle-archived',
      value: 'toggle-archived',
      icon: archivedView.value ? 'i-lucide-list' : 'i-lucide-archive',
    });
    sections.push({ items: adminItems });
  }
  return sections;
});

const leadQueryParams = computed(() => ({
  pipeline_id: pipeline.value?.id,
  search: search.value,
  assignee_id: assigneeFilter.value,
  status: statusFilter.value,
  created_from: createdFrom.value,
  created_to: createdTo.value,
  has_pending_task: hasPendingTask.value,
  archived: archivedView.value ? 'true' : '',
}));

// Pipeline a mostrar: el último elegido por el usuario (ui_settings) si sigue
// existiendo, si no el default, si no el primero.
const pickPipeline = () => {
  const lastId = uiSettings.value?.last_pipeline_id;
  return (
    pipelines.value.find(p => p.id === lastId) ||
    pipelines.value.find(p => p.is_default) ||
    pipelines.value[0] ||
    null
  );
};

// Carga (o recarga) la primera página de una columna; o la siguiente si !reset.
const loadColumnLeads = async (column, reset = false) => {
  const page = reset ? 1 : column.page + 1;
  column.loadingMore = true;
  try {
    const { data } = await LeadsAPI.get({
      ...leadQueryParams.value,
      stage_id: column.stage.id,
      page,
    });
    const leads = unwrap(data);
    column.leads = reset ? leads : [...column.leads, ...leads];
    column.page = page;
    column.hasMore = leads.length === LEADS_PER_PAGE;
  } catch (error) {
    useAlert(t('LEADS.LOAD_ERROR'));
  } finally {
    column.loadingMore = false;
  }
};

// Trae conteo + suma por etapa (server-side) y los vuelca en las columnas.
const applyStats = async () => {
  const { data } = await LeadsAPI.stats(leadQueryParams.value);
  const byStage = {};
  unwrap(data).forEach(s => {
    byStage[s.stage_id] = s;
  });
  columns.value.forEach(column => {
    column.count = byStage[column.stage.id]?.count || 0;
    column.valueSum = byStage[column.stage.id]?.value_sum || 0;
  });
};

// Arma las columnas (etapas + stats) y carga la 1ª página de cada una.
const buildColumns = async () => {
  const [{ data: stagesData }] = await Promise.all([
    PipelinesAPI.getStages(pipeline.value.id),
  ]);
  columns.value = unwrap(stagesData).map(stage => ({
    id: stage.id,
    stage,
    leads: [],
    count: 0,
    valueSum: 0,
    page: 0,
    hasMore: false,
    loadingMore: false,
  }));
  // Etapa activa en móvil: mantener la actual si sigue existiendo, si no la 1ª.
  if (!columns.value.some(c => c.stage.id === activeStageId.value)) {
    activeStageId.value = columns.value[0]?.stage.id ?? null;
  }
  await applyStats();
  await Promise.all(columns.value.map(column => loadColumnLeads(column, true)));
};

// Columna (etapa) activa en el layout móvil.
const activeColumn = computed(
  () =>
    columns.value.find(c => c.stage.id === activeStageId.value) ||
    columns.value[0] ||
    null
);

// Recarga stats + 1ª página de cada columna (al cambiar filtros / archivados),
// sin re-pedir las etapas.
const reloadLeads = async () => {
  if (!pipeline.value) return;
  await applyStats();
  await Promise.all(columns.value.map(column => loadColumnLeads(column, true)));
};

// --- Panel de widgets superior ---
const showWidgets = computed(
  () => uiSettings.value.show_pipeline_widgets !== false
);
const toggleWidgets = () =>
  updateUISettings({ show_pipeline_widgets: !showWidgets.value });

// "Resumen": UN solo Button + DropdownMenu para las estadísticas — mostrar/ocultar y
// su periodo. El periodo vive aquí adentro para dejar claro que aplica SOLO al resumen,
// no al tablero de leads.
const showResumenMenu = ref(false);
const resumenMenuSections = computed(() => [
  {
    title: t('LEADS.WIDGETS.TOGGLE'),
    items: PERIOD_OPTIONS.map(p => ({
      label: t(`LEADS.WIDGETS.PERIOD.${p.toUpperCase()}`),
      action: 'period',
      value: p,
      isSelected: period.value === p,
    })),
  },
  {
    items: [
      {
        label: t(
          showWidgets.value ? 'LEADS.WIDGETS.HIDE' : 'LEADS.WIDGETS.SHOW'
        ),
        action: 'toggle',
        value: 'toggle',
        icon: showWidgets.value ? 'i-lucide-eye-off' : 'i-lucide-eye',
      },
    ],
  },
]);
const onResumenAction = ({ action, value }) => {
  if (action === 'toggle') {
    toggleWidgets();
  } else if (action === 'period') {
    period.value = value;
    if (!showWidgets.value) updateUISettings({ show_pipeline_widgets: true });
  }
  showResumenMenu.value = false;
};

// Widget de tareas → Agenda con preset; widget de leads → filtra el tablero
// (el watcher de filtros recarga). Reseteamos los filtros de navegación previos.
const onNavigateAgenda = ({ preset }) => {
  router.push({ name: 'tasks_agenda', query: { preset } });
};

const onNavigateBoard = filters => {
  createdFrom.value = '';
  createdTo.value = '';
  hasPendingTask.value = '';
  statusFilter.value = filters.status ?? '';
  if (filters.has_pending_task !== undefined) {
    hasPendingTask.value = filters.has_pending_task;
  }
  if (filters.period) {
    const range = periodDateRange(filters.period);
    createdFrom.value = range.from;
    createdTo.value = range.to;
  }
};

const loadBoard = async () => {
  isLoading.value = true;
  try {
    const { data } = await PipelinesAPI.get();
    pipelines.value = unwrap(data);
    pipeline.value = pickPipeline();
    if (!pipeline.value) {
      columns.value = [];
      return;
    }
    await buildColumns();
  } catch (error) {
    useAlert(t('LEADS.LOAD_ERROR'));
  } finally {
    isLoading.value = false;
  }
};

// Mover un lead a otra etapa (móvil, acción explícita). Optimista + persiste.
const openMoveDialog = lead => {
  leadToMove.value = lead;
  moveDialog.value.open();
};

const moveLeadToStage = async stageId => {
  const lead = leadToMove.value;
  moveDialog.value.close();
  if (!lead || stageId === lead.stage_id) return;
  const src = columns.value.find(c => c.stage.id === lead.stage_id);
  const tgt = columns.value.find(c => c.stage.id === stageId);
  const value = Number(lead.value) || 0;
  if (src) {
    src.leads = src.leads.filter(l => l.id !== lead.id);
    src.count = Math.max(0, src.count - 1);
    src.valueSum -= value;
  }
  if (tgt) {
    tgt.leads.unshift(lead);
    tgt.count += 1;
    tgt.valueSum += value;
  }
  lead.stage_id = stageId;
  try {
    await LeadsAPI.move(lead.id, stageId, 10);
  } catch (error) {
    useAlert(t('LEADS.MOVE_ERROR'));
    loadBoard();
  }
};

// ---- Selector y gestión de pipelines (multi-pipeline) ----
const switchPipeline = async id => {
  if (pipeline.value?.id === id) return;
  const next = pipelines.value.find(p => p.id === id);
  if (!next) return;
  pipeline.value = next;
  updateUISettings({ last_pipeline_id: id });
  isLoading.value = true;
  try {
    await buildColumns();
  } catch (error) {
    useAlert(t('LEADS.LOAD_ERROR'));
  } finally {
    isLoading.value = false;
  }
};

const openPipelineDialog = mode => {
  pipelineDialogMode.value = mode;
  pipelineNameInput.value = mode === 'rename' ? pipeline.value?.name || '' : '';
  pipelineDialog.value.open();
};

const submitPipelineDialog = async () => {
  const name = pipelineNameInput.value.trim();
  if (!name) return;
  try {
    if (pipelineDialogMode.value === 'create') {
      const { data } = await PipelinesAPI.create({ pipeline: { name } });
      const created = unwrap(data);
      await updateUISettings({ last_pipeline_id: created.id });
      pipelineDialog.value.close();
      await loadBoard();
    } else {
      await PipelinesAPI.update(pipeline.value.id, { pipeline: { name } });
      pipeline.value.name = name;
      const inList = pipelines.value.find(p => p.id === pipeline.value.id);
      if (inList) inList.name = name;
      pipelineDialog.value.close();
    }
  } catch (error) {
    useAlert(t('LEADS.PIPELINE.ERROR'));
  }
};

const setDefaultPipeline = async () => {
  try {
    await PipelinesAPI.update(pipeline.value.id, {
      pipeline: { is_default: true },
    });
    // Refresca los flags is_default sin perder las columnas cargadas.
    const { data } = await PipelinesAPI.get();
    pipelines.value = unwrap(data);
    pipeline.value =
      pipelines.value.find(p => p.id === pipeline.value.id) || pipeline.value;
    useAlert(t('LEADS.PIPELINE.DEFAULT_SUCCESS'));
  } catch (error) {
    useAlert(t('LEADS.PIPELINE.ERROR'));
  }
};

const handleDeletePipeline = async () => {
  try {
    await PipelinesAPI.delete(pipeline.value.id);
    deletePipelineDialog.value.close();
    await updateUISettings({ last_pipeline_id: null });
    await loadBoard();
  } catch (error) {
    useAlert(error?.response?.data?.error || t('LEADS.PIPELINE.ERROR'));
  }
};

// Alterna el tablero entre activos y archivados (solo admin). Recarga columnas.
const toggleArchivedView = async () => {
  archivedView.value = !archivedView.value;
  if (!pipeline.value) return;
  isLoading.value = true;
  try {
    await reloadLeads();
  } catch (error) {
    useAlert(t('LEADS.LOAD_ERROR'));
  } finally {
    isLoading.value = false;
  }
};

const handlePipelineMenuAction = ({ action, value }) => {
  showPipelineMenu.value = false;
  if (action === 'switch') switchPipeline(value);
  else if (action === 'create') openPipelineDialog('create');
  else if (action === 'rename') openPipelineDialog('rename');
  else if (action === 'set-default') setDefaultPipeline();
  else if (action === 'delete') deletePipelineDialog.value.open();
  else if (action === 'toggle-archived') toggleArchivedView();
};

const clearFilters = () => {
  search.value = '';
  assigneeFilter.value = '';
  statusFilter.value = '';
};

// Exporta los leads del pipeline actual (respeta los filtros activos) a CSV.
const exportLeads = async () => {
  isExporting.value = true;
  try {
    const { data } = await LeadsAPI.exportLeads(leadQueryParams.value);
    const slug = (pipeline.value?.name || 'leads')
      .toLowerCase()
      .replace(/\s+/g, '-');
    downloadBlob(data, `leads-${slug}.csv`);
  } catch (error) {
    useAlert(t('LEADS.EXPORT.ERROR'));
  } finally {
    isExporting.value = false;
  }
};

// Búsqueda con debounce; agente/estado aplican al instante (recargan stats + pág.1).
let searchTimer = null;
watch(search, () => {
  clearTimeout(searchTimer);
  searchTimer = setTimeout(reloadLeads, 300);
});
watch(
  [assigneeFilter, statusFilter, createdFrom, createdTo, hasPendingTask],
  reloadLeads
);

// Tuning del drag (compartido por ambos Draggables). Se pasa como objeto por
// v-bind a propósito: `delayOnTouchOnly` debe llegar como booleano `true` para
// que el delay aplique SOLO en táctil (long-press para arrastrar vs. swipe para
// scroll) y el mouse en desktop arrastre inmediato. Como atributo "shorthand"
// (delay-on-touch-only) vuedraggable lo recibe como "" (falsy) vía $attrs y el
// delay terminaría aplicando también al mouse (drag lento en desktop).
const dragTuning = {
  delay: 180,
  delayOnTouchOnly: true,
  touchStartThreshold: 8,
};

// ---- Lead drag entre etapas (ajusta conteos/sumas server-side localmente) ----
const onLeadChange = async (event, column) => {
  // vuedraggable dispara 'removed' en la columna origen y 'added' en la destino.
  if (event.removed) {
    column.count = Math.max(0, column.count - 1);
    column.valueSum -= Number(event.removed.element.value) || 0;
    return; // el move lo dispara el 'added' del destino
  }
  const moved = event.added || event.moved;
  if (!moved) return;

  const lead = moved.element;
  const position = (moved.newIndex + 1) * 10;
  if (event.added) {
    column.count += 1;
    column.valueSum += Number(lead.value) || 0;
    lead.stage_id = column.stage.id;
  }
  try {
    await LeadsAPI.move(lead.id, column.stage.id, position);
  } catch (error) {
    useAlert(t('LEADS.MOVE_ERROR'));
    loadBoard();
  }
};

// ---- Lead create / edit (ambos = página LeadDetail) ----
const openCreateLead = () =>
  router.push({ name: 'leads_new', params: { accountId: accountId.value } });
const openEditLead = lead =>
  router.push({
    name: 'leads_show',
    params: { accountId: accountId.value, leadId: lead.id },
  });

const openConversation = lead => {
  if (!lead.conversation) return;
  router.push({
    name: 'inbox_conversation',
    params: {
      accountId: accountId.value,
      conversation_id: lead.conversation.display_id,
    },
  });
};

// ---- Stage management (admin) ----
const startRenameStage = stage => {
  if (!isAdmin.value) return;
  editingStageId.value = stage.id;
  editingStageName.value = stage.name;
};

const saveStageName = async stage => {
  const name = editingStageName.value.trim();
  editingStageId.value = null;
  if (!name || name === stage.name) return;
  try {
    await PipelinesAPI.updateStage(pipeline.value.id, stage.id, {
      pipeline_stage: { name },
    });
    stage.name = name;
  } catch (error) {
    useAlert(t('LEADS.STAGE.ERROR'));
  }
};

const addStage = async () => {
  try {
    const { data } = await PipelinesAPI.createStage(pipeline.value.id, {
      pipeline_stage: { name: t('LEADS.STAGE.NEW_NAME') },
    });
    const stage = unwrap(data);
    columns.value.push({ id: stage.id, stage, leads: [] });
    startRenameStage(stage);
  } catch (error) {
    useAlert(t('LEADS.STAGE.ERROR'));
  }
};

const confirmDeleteStage = stage => {
  stageToDelete.value = stage;
  deleteStageDialog.value.open();
};

const handleDeleteStage = async () => {
  if (!stageToDelete.value) return;
  try {
    await PipelinesAPI.deleteStage(pipeline.value.id, stageToDelete.value.id);
    deleteStageDialog.value.close();
    stageToDelete.value = null;
    await loadBoard();
  } catch (error) {
    useAlert(t('LEADS.STAGE.ERROR'));
  }
};

const onColumnReorder = async event => {
  // @change en el Draggable externo solo dispara al reordenar columnas; el
  // guard evita actuar si el evento no es un movimiento de columna.
  if (event && !event.moved) return;

  const positionsHash = {};
  columns.value.forEach((column, index) => {
    const position = (index + 1) * 10;
    positionsHash[column.stage.id] = position;
    column.stage.position = position;
  });
  try {
    await PipelinesAPI.reorderStages(pipeline.value.id, positionsHash);
  } catch (error) {
    useAlert(t('LEADS.STAGE.ERROR'));
    loadBoard();
  }
};

onMounted(() => {
  store.dispatch('agents/get');
  loadBoard();
});
</script>

<template>
  <div class="flex flex-col w-full h-full bg-n-background">
    <PageHeader
      v-model:search="search"
      :search-placeholder="pipeline ? t('LEADS.FILTER.SEARCH_PLACEHOLDER') : ''"
    >
      <template #title>
        <h1 class="flex-shrink-0 text-xl font-semibold text-n-slate-12">
          {{ t('LEADS.TITLE') }}
        </h1>
        <div
          v-if="pipeline"
          v-on-clickaway="() => (showPipelineMenu = false)"
          class="relative flex items-center min-w-0 gap-1.5"
        >
          <span class="flex-shrink-0 text-n-slate-9">/</span>
          <!-- Pipeline selector trigger: kept as <button> (not Button component) because
               it lives inline in the PageHeader #title slot alongside a <h1> and needs
               min-w-0 truncation on its text child — Button's own layout classes would
               override this. Tokens are n-* throughout. -->
          <button
            class="flex items-center min-w-0 gap-1 text-sm rounded-md text-n-slate-11 hover:text-n-slate-12"
            @click="showPipelineMenu = !showPipelineMenu"
          >
            <span class="truncate">{{ pipeline.name }}</span>
            <span class="flex-shrink-0 i-lucide-chevron-down size-3.5" />
          </button>
          <span
            v-if="archivedView"
            class="inline-flex flex-shrink-0 items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full bg-n-amber-3 text-n-amber-11"
          >
            <span class="i-lucide-archive size-3" />
            {{ t('LEADS.PIPELINE.ARCHIVED_BADGE') }}
          </span>
          <DropdownMenu
            v-if="showPipelineMenu"
            :menu-sections="pipelineMenuSections"
            class="left-0 top-8"
            @action="handlePipelineMenuAction"
          />
        </div>
      </template>

      <!-- Orden: (búsqueda, la pone PageHeader) · Filtros · Resumen · Periodo · Nuevo.
           Todos Button md (40px) → alturas parejas sin overrides. -->
      <template v-if="pipeline" #actions>
        <div class="relative">
          <Button
            slate
            ghost
            icon="i-lucide-sliders-horizontal"
            :title="t('LEADS.FILTER.TITLE')"
            @click="filtersDialog.open()"
          />
          <span
            v-if="assigneeFilter || statusFilter"
            class="absolute rounded-full pointer-events-none top-1 right-1 size-1.5 bg-n-brand"
          />
        </div>
        <!-- Resumen: un solo Button + DropdownMenu (mostrar/ocultar + periodo de las
             estadísticas). El periodo NO toca el tablero de leads, solo el resumen. -->
        <div
          v-if="!archivedView"
          v-on-clickaway="() => (showResumenMenu = false)"
          class="relative"
        >
          <Button
            :color="showWidgets ? 'blue' : 'slate'"
            :variant="showWidgets ? 'faded' : 'ghost'"
            icon="i-lucide-layout-dashboard"
            :title="t('LEADS.WIDGETS.TOGGLE')"
            @click="showResumenMenu = !showResumenMenu"
          />
          <DropdownMenu
            v-if="showResumenMenu"
            :menu-sections="resumenMenuSections"
            class="right-0 top-11 min-w-[12rem]"
            @action="onResumenAction"
          />
        </div>
        <Button
          v-if="!archivedView"
          blue
          solid
          icon="i-lucide-plus"
          :title="t('LEADS.NEW_SHORT')"
          class="md:hidden"
          @click="openCreateLead"
        />
        <Button
          v-if="!archivedView"
          blue
          solid
          icon="i-lucide-plus"
          :label="t('LEADS.NEW_SHORT')"
          class="hidden md:flex"
          @click="openCreateLead"
        />
      </template>
    </PageHeader>

    <div v-if="isLoading" class="flex items-center justify-center flex-1">
      <Spinner />
    </div>

    <div
      v-else-if="!pipeline"
      class="flex items-center justify-center flex-1 text-n-slate-11"
    >
      {{ t('LEADS.EMPTY') }}
    </div>

    <template v-else>
      <!-- Panel de widgets (el toggle "Resumen" vive en el header principal) -->
      <div
        v-if="!archivedView && showWidgets"
        class="flex-shrink-0 px-4 pt-3 sm:px-6"
      >
        <PipelineWidgets
          :pipeline-id="pipeline.id"
          :currency="pipeline.currency"
          :assignee-id="assigneeFilter"
          :period="period"
          @navigate-agenda="onNavigateAgenda"
          @navigate-board="onNavigateBoard"
        />
      </div>

      <!-- Desktop/tablet: Kanban horizontal con drag (long-press en táctil) -->
      <div class="flex-1 hidden p-4 overflow-x-auto overflow-y-hidden md:block">
        <div class="flex items-start h-full gap-4 min-w-max">
          <Draggable
            :list="columns"
            item-key="id"
            handle=".stage-grip"
            :group="{ name: 'stages' }"
            :disabled="!isAdmin"
            v-bind="dragTuning"
            class="flex items-start h-full gap-4"
            @change="onColumnReorder"
          >
            <template #item="{ element: column }">
              <div
                class="flex flex-col flex-shrink-0 rounded-xl w-72 max-h-full bg-n-alpha-1"
              >
                <div
                  class="flex items-center justify-between gap-1 px-3 py-2.5 border-b border-n-weak"
                >
                  <div class="flex items-center min-w-0 gap-1.5">
                    <span
                      v-if="isAdmin"
                      class="flex-shrink-0 cursor-grab stage-grip i-lucide-grip-vertical size-3.5 text-n-slate-9"
                    />
                    <span
                      class="flex-shrink-0 rounded-full size-2.5"
                      :style="{
                        backgroundColor: column.stage.color || '#94A3B8',
                      }"
                    />
                    <Input
                      v-if="editingStageId === column.stage.id"
                      v-model="editingStageName"
                      autofocus
                      custom-input-class="!py-0.5 !text-sm"
                      @keyup.enter="saveStageName(column.stage)"
                      @blur="saveStageName(column.stage)"
                    />
                    <span
                      v-else
                      class="text-sm font-medium truncate text-n-slate-12"
                      :class="{ 'cursor-text': isAdmin }"
                      @click="startRenameStage(column.stage)"
                    >
                      {{ column.stage.name }}
                    </span>
                    <span class="text-xs text-n-slate-10">
                      {{ column.count }}
                    </span>
                  </div>
                  <div class="flex items-center flex-shrink-0 gap-1">
                    <span
                      v-if="column.valueSum > 0"
                      class="text-xs font-semibold text-n-teal-11"
                    >
                      {{
                        formatLeadCurrency(column.valueSum, pipeline?.currency)
                      }}
                    </span>
                    <Button
                      v-if="isAdmin && column.stage.deletable"
                      ghost
                      icon="i-lucide-trash-2"
                      size="xs"
                      class="text-n-slate-10 hover:text-n-ruby-11"
                      @click.stop="confirmDeleteStage(column.stage)"
                    />
                  </div>
                </div>

                <Draggable
                  :list="column.leads"
                  item-key="id"
                  :group="{ name: 'leads' }"
                  :disabled="archivedView"
                  v-bind="dragTuning"
                  class="flex flex-col flex-1 gap-2 p-2 overflow-y-auto min-h-[60px]"
                  ghost-class="opacity-40"
                  @change="onLeadChange($event, column)"
                >
                  <template #item="{ element }">
                    <LeadCard
                      :lead="element"
                      @open="openConversation"
                      @edit="openEditLead"
                    />
                  </template>
                </Draggable>

                <Button
                  v-if="column.hasMore"
                  ghost
                  color="slate"
                  size="sm"
                  class="flex-shrink-0 mx-2 mb-2"
                  :is-loading="column.loadingMore"
                  :label="
                    t('LEADS.LOAD_MORE', {
                      count: column.count - column.leads.length,
                    })
                  "
                  @click="loadColumnLeads(column, false)"
                />
              </div>
            </template>
          </Draggable>

          <!-- Kept as Button component for consistent ghost styling. -->
          <Button
            v-if="isAdmin && pipeline"
            ghost
            color="slate"
            icon="i-lucide-plus"
            :label="t('LEADS.STAGE.ADD')"
            class="flex-shrink-0 rounded-xl"
            @click="addStage"
          />
        </div>
      </div>

      <!-- Móvil: una etapa a la vez (chips de navegación + lista vertical + mover) -->
      <div class="flex flex-col flex-1 min-h-0 md:hidden">
        <div
          class="flex flex-shrink-0 gap-2 px-3 py-2 overflow-x-auto border-b border-n-weak"
        >
          <!-- Stage chip: kept as <button> (not Button component) because the active
               state needs a custom pill shape (rounded-full) + brand background that
               doesn't map cleanly to any Button variant. Layout is structural. -->
          <button
            v-for="column in columns"
            :key="column.id"
            class="flex items-center flex-shrink-0 gap-1.5 px-3 py-1.5 text-sm rounded-full"
            :class="
              column.stage.id === activeStageId
                ? 'bg-n-brand text-n-solid-1'
                : 'bg-n-alpha-1 text-n-slate-11'
            "
            @click="activeStageId = column.stage.id"
          >
            <span
              class="rounded-full size-2"
              :style="{ backgroundColor: column.stage.color || '#94A3B8' }"
            />
            {{ column.stage.name }}
            <span class="text-xs opacity-80">{{ column.count }}</span>
          </button>
        </div>

        <div
          v-if="activeColumn"
          class="flex flex-col flex-1 gap-2 p-3 overflow-y-auto"
        >
          <div
            v-if="activeColumn.valueSum > 0"
            class="text-xs font-semibold text-n-teal-11"
          >
            {{ formatLeadCurrency(activeColumn.valueSum, pipeline?.currency) }}
          </div>
          <LeadCard
            v-for="lead in activeColumn.leads"
            :key="lead.id"
            :lead="lead"
            movable
            @open="openConversation"
            @edit="openEditLead"
            @move="openMoveDialog"
          />
          <p
            v-if="!activeColumn.leads.length"
            class="py-6 text-sm text-center text-n-slate-10"
          >
            {{ t('LEADS.EMPTY_STAGE') }}
          </p>
          <Button
            v-if="activeColumn.hasMore"
            ghost
            color="slate"
            size="sm"
            :is-loading="activeColumn.loadingMore"
            :label="
              t('LEADS.LOAD_MORE', {
                count: activeColumn.count - activeColumn.leads.length,
              })
            "
            @click="loadColumnLeads(activeColumn, false)"
          />
        </div>
      </div>
    </template>

    <!-- Picker de etapa para mover un lead (móvil) -->
    <Dialog ref="moveDialog" :title="t('LEADS.MOVE_TITLE')">
      <div class="flex flex-col gap-1">
        <button
          v-for="column in columns"
          :key="column.id"
          class="flex items-center gap-2 px-3 py-2 text-sm text-left rounded-lg hover:bg-n-alpha-2 disabled:opacity-40"
          :disabled="leadToMove && column.stage.id === leadToMove.stage_id"
          @click="moveLeadToStage(column.stage.id)"
        >
          <span
            class="rounded-full size-2"
            :style="{ backgroundColor: column.stage.color || '#94A3B8' }"
          />
          {{ column.stage.name }}
        </button>
      </div>
      <template #footer><span /></template>
    </Dialog>

    <!-- Modal de filtros (móvil): agente + estado + exportar/limpiar -->
    <Dialog
      ref="filtersDialog"
      :title="t('LEADS.FILTER.TITLE')"
      :confirm-button-label="t('LEADS.FILTER.DONE')"
      @confirm="filtersDialog.close()"
    >
      <div class="flex flex-col gap-4">
        <label v-if="hasFullLeadAccess" class="flex flex-col gap-1.5">
          <span class="text-sm font-medium text-n-slate-12">
            {{ t('LEADS.FILTER.AGENT') }}
          </span>
          <Select
            v-model="assigneeFilter"
            class="w-full"
            :options="assigneeOptions"
          />
        </label>
        <label class="flex flex-col gap-1.5">
          <span class="text-sm font-medium text-n-slate-12">
            {{ t('LEADS.FILTER.STATUS') }}
          </span>
          <Select
            v-model="statusFilter"
            class="w-full"
            :options="statusOptions"
          />
        </label>
        <Button
          slate
          faded
          icon="i-lucide-download"
          :label="t('LEADS.EXPORT.LABEL')"
          :is-loading="isExporting"
          @click="exportLeads"
        />
        <Button
          v-if="hasActiveFilters"
          link
          :label="t('LEADS.FILTER.CLEAR')"
          @click="clearFilters"
        />
      </div>
    </Dialog>

    <Dialog
      ref="deleteStageDialog"
      type="alert"
      :title="t('LEADS.STAGE.DELETE_TITLE')"
      :description="t('LEADS.STAGE.DELETE_CONFIRM')"
      :confirm-button-label="t('LEADS.STAGE.DELETE')"
      @confirm="handleDeleteStage"
    />

    <Dialog
      ref="pipelineDialog"
      :title="
        pipelineDialogMode === 'create'
          ? t('LEADS.PIPELINE.NEW')
          : t('LEADS.PIPELINE.RENAME')
      "
      :confirm-button-label="t('LEADS.PIPELINE.SAVE')"
      @confirm="submitPipelineDialog"
    >
      <Input
        v-model="pipelineNameInput"
        :label="t('LEADS.PIPELINE.NAME_LABEL')"
        :placeholder="t('LEADS.PIPELINE.NAME_PLACEHOLDER')"
      />
    </Dialog>

    <Dialog
      ref="deletePipelineDialog"
      type="alert"
      :title="t('LEADS.PIPELINE.DELETE_TITLE')"
      :description="t('LEADS.PIPELINE.DELETE_CONFIRM')"
      :confirm-button-label="t('LEADS.PIPELINE.DELETE')"
      @confirm="handleDeletePipeline"
    />
  </div>
</template>
